import React, { useEffect, useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { enUS, de, fr } from 'date-fns/locale';
import { CalendarBlank } from '@phosphor-icons/react';
import { Button } from './Button';
import { LocaleMap } from '../../Types';
import 'react-day-picker/style.css';

const locales: LocaleMap = {
    en: enUS,
    de: de,
    fr: fr,
};

interface DateRangePickerProps {
    timeRange: { from?: Date; to?: Date };
    selectedLanguage: string;
    i18n: any;
    onTimeRangeChange: (range: { from: Date; to: Date }) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ timeRange, selectedLanguage, i18n, onTimeRangeChange }) => {
    const [showDatePicker, setShowDatePicker] = React.useState(false);
    const [showTimeRangeDropdown, setShowTimeRangeDropdown] = React.useState(false);
    const [selectedRange, setSelectedRange] = React.useState<{ from: Date; to: Date } | undefined>(
        timeRange.from && timeRange.to ? { from: timeRange.from, to: timeRange.to } : undefined,
    );
    const [selectedTimeRangeLabel, setSelectedTimeRangeLabel] = React.useState<string | null>(null);
    const datePickerRef = useRef<HTMLDivElement>(null);
    const showDatePickerRef = useRef(showDatePicker);
    const showTimeRangeDropdownRef = useRef(showTimeRangeDropdown);

    useEffect(() => {
        showDatePickerRef.current = showDatePicker;
    }, [showDatePicker]);

    useEffect(() => {
        showTimeRangeDropdownRef.current = showTimeRangeDropdown;
    }, [showTimeRangeDropdown]);

    useEffect(() => {
        if (timeRange.from && timeRange.to) {
            setSelectedRange({ from: timeRange.from, to: timeRange.to });
        }
    }, [timeRange]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!showDatePickerRef.current && !showTimeRangeDropdownRef.current) return;

            if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
                setShowDatePicker(false);
                setShowTimeRangeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatDateRange = (from?: Date, to?: Date) => {
        if (!from || !to) return i18n.translate({ id: 'datepicker.placeholder', sourceName: 'Dashboard' });
        return `${format(from, 'dd.MM.yyyy')} - ${format(to, 'dd.MM.yyyy')}`;
    };

    const timeRanges = [
        {
            key: 'today',
            label: i18n.translate({ id: 'datepicker.today', sourceName: 'Dashboard' }),
            from: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0),
            to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
        },
        {
            key: 'yesterday',
            label: i18n.translate({ id: 'datepicker.yesterday', sourceName: 'Dashboard' }),
            from: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 1, 0, 0, 0),
            to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 1, 23, 59, 59),
        },
        {
            key: 'lastSevenDays',
            label: i18n.translate({ id: 'datepicker.last7Days', sourceName: 'Dashboard' }),
            from: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 6, 0, 0, 0),
            to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
        },
        {
            key: 'currentMonth',
            label: i18n.translate({ id: 'datepicker.currentMonth', sourceName: 'Dashboard' }),
            from: new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0),
            to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
        },
        {
            key: 'lastMonth',
            label: i18n.translate({ id: 'datepicker.lastMonth', sourceName: 'Dashboard' }),
            from: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1, 0, 0, 0),
            to: new Date(new Date().getFullYear(), new Date().getMonth(), 0, 23, 59, 59),
        },
        {
            key: 'lastThreeMonths',
            label: i18n.translate({ id: 'datepicker.last3Months', sourceName: 'Dashboard' }),
            from: new Date(new Date().getFullYear(), new Date().getMonth() - 3, new Date().getDate() + 1, 0, 0, 0),
            to: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59),
        },
    ];

    const handleTimeRangeSelect = (range: { from: Date; to: Date }, label: string) => {
        onTimeRangeChange(range);
        setSelectedRange(range);
        setSelectedTimeRangeLabel(label);
        setShowTimeRangeDropdown(false);
    };

    const handleDateSelect = (range: any) => {
        if (!range) {
            setSelectedRange(undefined);
            return;
        }

        setSelectedRange(range);
        setSelectedTimeRangeLabel(null);

        if (range.from && range.to) {
            onTimeRangeChange(range);
        }
    };

    return (
        <div ref={datePickerRef} className="w-full md:w-fit relative">
            <div className="flex w-full md:w-auto">
                <Button
                    variant="secondaryOutlined"
                    className="!m-0 rounded-lg !border !border-r-0 rounded-r-none !py-2 !pl-2 !pr-10 flex items-center gap-2 min-w-[180px] flex-1 md:flex-none"
                    onClick={() => {
                        setShowDatePicker(!showDatePicker);
                        setShowTimeRangeDropdown(false);
                    }}
                >
                    <CalendarBlank />
                    {formatDateRange(timeRange.from, timeRange.to)}
                </Button>
                <Button
                    variant="primary"
                    className="!m-0 text-center rounded-lg border !border-layout-borderColor min-w-[120px] !border-l-0 rounded-l-none !py-1 !px-5 ml-2 flex items-center gap-2 md:flex-none"
                    onClick={() => {
                        setShowTimeRangeDropdown(!showTimeRangeDropdown);
                        setShowDatePicker(false);
                    }}
                >
                    <span className="w-full text-center">
                        {selectedTimeRangeLabel ||
                            i18n.translate({ id: 'datepicker.selectPlaceholder', sourceName: 'Dashboard' })}
                    </span>
                </Button>
            </div>

            {showDatePicker && (
                <div className="absolute z-10 mt-2 bg-layout-bodyBackground border border-layout-borderColor rounded-lg shadow-lg p-4">
                    <DayPicker
                        mode="range"
                        selected={selectedRange}
                        onSelect={handleDateSelect}
                        captionLayout="dropdown-years"
                        navLayout="around"
                        fromYear={2020}
                        endMonth={new Date()}
                        disabled={(afterToday) => afterToday > new Date()}
                        max={new Date().getDate()}
                        locale={locales[selectedLanguage] || enUS}
                        weekStartsOn={1}
                        showOutsideDays
                        classNames={{
                            selected: `bg-datePickerSelectedColor text-primary rounded-none !py-0 !text-center hover:bg-datePickerSelectedColor hover:text-primary`,
                            range_start: `bg-datePickerSelectedColor rounded-l-lg`,
                            range_end: `bg-datePickerSelectedColor rounded-r-lg`,
                            day: `hover:bg-datePickerHoverColor w-9 h-9 !py-0 rounded-lg`,
                            day_button: `h-9 w-9 disabled:cursor-not-allowed disabled:opacity-50`,
                            today: `text-inherit`,
                            week: `border border-[5px] border-layout-bodyBackground`,
                            outside: `text-gray-500`,
                            chevron: `fill-layout-textcolor`,
                            years_dropdown: `bg-layout-bodyBackground border border-layout-borderColor rounded-lg shadow-lg`,
                            weekday: `!text-center`,
                        }}
                    />
                </div>
            )}

            {showTimeRangeDropdown && (
                <div className="absolute z-10 mt-2 bg-layout-bodyBackground border border-layout-borderColor rounded-lg shadow-lg w-full right-0">
                    {timeRanges.map((range) => (
                        <div
                            key={range.key}
                            className="px-4 py-2 hover:bg-datePickerHoverColor cursor-pointer"
                            onClick={() => handleTimeRangeSelect({ from: range.from, to: range.to }, range.label)}
                        >
                            {range.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
